import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { APP_VERSION } from "@/lib/appVersion";

const VERSION_ENDPOINT = `${import.meta.env.BASE_URL}version.json`;
const POLL_INTERVAL_MS = 5 * 60 * 1000;
const UPDATE_RELOAD_GUARD_KEY = "aleaedge.update.reloadInProgress";
const CACHE_RESET_QUERY_PARAM = "cache-reset";
const SHOULD_LOG_UPDATE_DEBUG = import.meta.env.DEV;

const logUpdateDebug = (message: string, context?: Record<string, unknown>) => {
  if (!SHOULD_LOG_UPDATE_DEBUG) return;
  console.debug(message, context);
};

export const CHECK_FOR_UPDATES_EVENT = "aleaedge:check-for-updates";
export const CHECK_FOR_UPDATES_RESULT_EVENT = "aleaedge:check-for-updates-result";

export type CheckForUpdatesResult = {
  updateAvailable: boolean;
  deployedVersion?: string;
  currentVersion: string;
  error?: boolean;
};

const getServiceWorkerRegistration = async (): Promise<ServiceWorkerRegistration | null> => {
  if (!("serviceWorker" in navigator)) return null;

  return (await navigator.serviceWorker.getRegistration()) ?? null;
};

const getServiceWorkerRegistrations = async (): Promise<readonly ServiceWorkerRegistration[]> => {
  if (!("serviceWorker" in navigator)) return [];

  return navigator.serviceWorker.getRegistrations();
};

const getWaitingServiceWorker = async (): Promise<ServiceWorker | null> => {
  const registration = await getServiceWorkerRegistration();
  return registration?.waiting ?? null;
};

const clearAllClientCaches = async () => {
  if (!("caches" in window)) return;

  const cacheKeys = await caches.keys();

  await Promise.allSettled(cacheKeys.map((key) => caches.delete(key)));
};

const unregisterServiceWorkers = async () => {
  const registrations = await getServiceWorkerRegistrations();

  await Promise.allSettled(registrations.map((registration) => registration.unregister()));
};

export const forceDeviceUpdateRefresh = async () => {
  try {
    await clearAllClientCaches();
  } catch (error) {
    logUpdateDebug("[AppUpdatePrompt] cache clear failed", { error });
  }

  try {
    await unregisterServiceWorkers();
  } catch (error) {
    logUpdateDebug("[AppUpdatePrompt] service worker unregister failed", { error });
  }

  const refreshUrl = new URL(window.location.href);
  refreshUrl.searchParams.set(CACHE_RESET_QUERY_PARAM, Date.now().toString());
  window.location.replace(refreshUrl.toString());
};

const reloadWithGuard = () => {
  try {
    if (sessionStorage.getItem(UPDATE_RELOAD_GUARD_KEY) === "1") return;
    sessionStorage.setItem(UPDATE_RELOAD_GUARD_KEY, "1");
  } catch {
    // Ignore storage issues.
  }

  void forceDeviceUpdateRefresh();
};

export default function AppUpdatePrompt() {
  const [isUpdateAvailable, setIsUpdateAvailable] = useState(false);
  const [waitingWorker, setWaitingWorker] = useState<ServiceWorker | null>(null);

  useEffect(() => {
    try {
      sessionStorage.removeItem(UPDATE_RELOAD_GUARD_KEY);
    } catch {
      // Ignore storage issues.
    }
  }, []);

  useEffect(() => {
    let timer: number | null = null;

    const checkVersion = async ({ forceServiceWorkerUpdate = false } = {}) => {
      let result: CheckForUpdatesResult = {
        updateAvailable: false,
        currentVersion: APP_VERSION,
      };

      if (forceServiceWorkerUpdate) {
        try {
          await (await getServiceWorkerRegistration())?.update();
        } catch (error) {
          logUpdateDebug("[AppUpdatePrompt] service worker update check failed", { error });
        }
      }

      try {
        const cacheBustedUrl = `${VERSION_ENDPOINT}?t=${Date.now()}`;
        const response = await fetch(cacheBustedUrl, {
          cache: "no-store",
          headers: { "cache-control": "no-cache", pragma: "no-cache" },
        });

        if (!response.ok) {
          logUpdateDebug("[AppUpdatePrompt] version fetch failed", {
            status: response.status,
            endpoint: VERSION_ENDPOINT,
          });
          result.error = true;
        } else {
          const deployed = (await response.json()) as { version?: string };
          result.deployedVersion = deployed.version;
          logUpdateDebug("[AppUpdatePrompt] version check", {
            endpoint: VERSION_ENDPOINT,
            deployedVersion: deployed.version,
            currentVersion: APP_VERSION,
          });

          if (deployed.version && deployed.version !== APP_VERSION) {
            setIsUpdateAvailable(true);
            result.updateAvailable = true;
          }
        }
      } catch (error) {
        logUpdateDebug("[AppUpdatePrompt] version check error", {
          endpoint: VERSION_ENDPOINT,
          error,
        });
        result.error = true;
      }

      try {
        const waiting = await getWaitingServiceWorker();
        if (waiting) {
          setWaitingWorker(waiting);
          setIsUpdateAvailable(true);
          result.updateAvailable = true;
        }
      } catch {
        // Best effort only.
      }

      window.dispatchEvent(
        new CustomEvent<CheckForUpdatesResult>(CHECK_FOR_UPDATES_RESULT_EVENT, { detail: result }),
      );
    };

    logUpdateDebug("[AppUpdatePrompt] mounted", {
      endpoint: VERSION_ENDPOINT,
      pollIntervalMs: POLL_INTERVAL_MS,
      currentVersion: APP_VERSION,
    });

    void checkVersion();

    timer = window.setInterval(() => {
      logUpdateDebug("[AppUpdatePrompt] polling tick", { endpoint: VERSION_ENDPOINT });
      void checkVersion();
    }, POLL_INTERVAL_MS);

    const onManualCheck = () => {
      logUpdateDebug("[AppUpdatePrompt] manual check triggered");
      void checkVersion({ forceServiceWorkerUpdate: true });
    };
    window.addEventListener(CHECK_FOR_UPDATES_EVENT, onManualCheck);

    return () => {
      if (timer) window.clearInterval(timer);
      window.removeEventListener(CHECK_FOR_UPDATES_EVENT, onManualCheck);
    };
  }, []);

  const handleRefreshNow = async () => {
    await clearAllClientCaches();

    if (waitingWorker) {
      let didReload = false;

      const onControllerChange = () => {
        if (didReload) return;
        didReload = true;
        navigator.serviceWorker.removeEventListener("controllerchange", onControllerChange);
        reloadWithGuard();
      };

      navigator.serviceWorker.addEventListener("controllerchange", onControllerChange);
      waitingWorker.postMessage({ type: "SKIP_WAITING" });

      window.setTimeout(() => {
        if (didReload) return;
        didReload = true;
        navigator.serviceWorker.removeEventListener("controllerchange", onControllerChange);
        reloadWithGuard();
      }, 4000);

      return;
    }

    reloadWithGuard();
  };

  const prompt = useMemo(() => {
    if (!isUpdateAvailable) return null;

    return (
      <div className="fixed bottom-4 left-1/2 z-[100] w-[calc(100%-2rem)] max-w-lg -translate-x-1/2 rounded-lg border bg-background p-4 shadow-lg">
        <p className="text-sm font-medium">
          A new AleaEdge update is available. Refresh to get the latest version.
        </p>
        <div className="mt-3 flex justify-end gap-2">
          <Button variant="outline" onClick={() => setIsUpdateAvailable(false)}>Later</Button>
          <Button onClick={() => void handleRefreshNow()}>Refresh now</Button>
        </div>
      </div>
    );
  }, [isUpdateAvailable, waitingWorker]);

  return prompt;
}
